/**
 * Web shim for react-native-webview.
 * Renders HTML content in an <iframe> (srcdoc). Manages its own height
 * by measuring the iframe document after load so it expands to fit content
 * with no internal scrollbar. Still fires onMessage so native-path code
 * (ReportView webViewHeights state) remains in sync.
 */
import React, { useState, useCallback, useRef } from 'react';

interface WebViewShimProps {
  source?: { html?: string; uri?: string };
  style?: { width?: string | number; height?: number; [key: string]: any };
  scrollEnabled?: boolean;
  injectedJavaScript?: string;
  onMessage?: (event: { nativeEvent: { data: string } }) => void;
  [key: string]: any;
}

const WebViewShim: React.FC<WebViewShimProps> = ({ source, onMessage }) => {
  // Start at 0 so there is no initial fixed-height phase visible to the user.
  const [contentHeight, setContentHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = e.currentTarget;
      try {
        const doc = iframe.contentDocument;
        const body = doc?.body;
        if (!body) return;

        const measure = () => {
          const height = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            doc?.documentElement?.scrollHeight ?? 0,
            1
          );
          setContentHeight(height);
          onMessage?.({ nativeEvent: { data: String(height) } });
        };

        measure();

        // Re-measure if the iframe content changes after initial load
        // (e.g. lazy-rendered elements, fonts).
        observerRef.current?.disconnect();
        const ro = new ResizeObserver(measure);
        ro.observe(body);
        observerRef.current = ro;
      } catch {
        // Cross-origin or unavailable — ignore
      }
    },
    [onMessage]
  );

  return (
    <iframe
      srcDoc={source?.html ?? ''}
      style={{
        width: '100%',
        height: contentHeight || 0,
        border: 'none',
        display: 'block',
        backgroundColor: 'transparent',
        overflow: 'hidden',
      }}
      // Prevent the iframe from showing its own scrollbar while content height
      // is being resolved or if measurement is slightly off.
      scrolling="no"
      onLoad={handleLoad}
      title="report-preview"
    />
  );
};

export default WebViewShim;
